import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sparkles, Sunrise, Users, PlayCircle, User } from 'lucide-react';
import { notificationService, useAuthStore } from '@vesper/shared';
import { resolveAvatarUrl } from '@/lib/avatarUrl';

/**
 * Vesper TabBar — ported from the Claude Design prototype (vesper-design TabBar).
 *
 * Floats 28px from the bottom, inset 12px on each side, glass-blur background,
 * 28px corner radius. Active tab: stroke 2 + white. Inactive: stroke 1.5 + white/45.
 *
 * Tabs: Cleo · Horizon · Rooms · Updates · You
 * Horizon = curation surface (Map reachable via its inline preview).
 * Rooms   = social home (umbrella for friends, group chats, DMs, upcoming events).
 */
const tabs = [
  { to: '/cleo',    match: ['/cleo', '/ark'],                       label: 'Cleo',    Icon: Sparkles },
  { to: '/horizon', match: ['/horizon', '/discover'],               label: 'Horizon', Icon: Sunrise },
  { to: '/rooms',   match: ['/rooms', '/connections', '/bench'],    label: 'Rooms',   Icon: Users },
  { to: '/updates', match: ['/updates'],                            label: 'Updates', Icon: PlayCircle },
  { to: '/you',     match: ['/you', '/profile'],                    label: 'You',     Icon: User },
] as const;

/** Profile-circle for the You tab. The label is hidden for this tab so the
 *  avatar reads as the identity itself — bigger than the stroke icons. Active
 *  state gets a Vesper-gold ring; inactive is dimmed + faint white ring. When
 *  no avatar is set, falls back to a circle with the user's initials. */
function YouTabIcon({ active }: { active: boolean }) {
  const user = useAuthStore((s) => s.user);
  const avatarUrl = user?.avatarUrl;
  const initials = (user?.displayName ?? '')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full overflow-hidden flex-none flex items-center justify-center"
      style={{
        width: 30,
        height: 30,
        background: avatarUrl ? '#2a2a3a' : '#3a2f5e',
        border: active ? '1.5px solid #E5B259' : '1px solid rgba(255,255,255,0.20)',
        color: '#a5b4fc',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {avatarUrl ? (
        <img
          src={resolveAvatarUrl(avatarUrl)}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: active ? 'none' : 'saturate(0.75) brightness(0.85)' }}
        />
      ) : (
        initials || <User size={16} strokeWidth={active ? 2 : 1.5} />
      )}
    </div>
  );
}

export default function BottomNav(): React.ReactElement {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    notificationService.getUnreadCount().then(setUnreadCount).catch(() => {});
  }, [location.pathname]);

  const isActive = (matches: readonly string[]) =>
    matches.some((m) => location.pathname === m || location.pathname.startsWith(m + '/'));

  return (
    // Outer wrapper is position:fixed so the nav is anchored to the viewport
    // (or the transformed PhoneFrame container when the iPhone frame is
    // enabled) instead of the .app-layout-content div above. Previously the
    // nav was position:absolute relative to that wrapper, which is fine on
    // h-screen pages like Updates but on pages that overflow (Rooms /
    // Horizon) the wrapper grew and `bottom: 20px` landed below the visible
    // viewport — users had to scroll down to find the nav.
    //
    // Inner div restores the 430-px-wide max constraint and the 24-px side
    // gutters so the look is identical across desktop preview + real mobile.
    // pointer-events-none on the outer / -auto on the inner keeps the
    // surrounding margin click-through.
    <div
      className="fixed inset-x-0 z-50 px-6 pointer-events-none"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
      }}
    >
      <div
        className="mx-auto flex items-center justify-between pointer-events-auto"
        style={{
          maxWidth: 382, // 430 phone column - 48 of side gutters = 382
          borderRadius: 24,
          padding: '3px 8px',
          background: 'rgba(15,15,28,0.78)',
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
          border: '0.5px solid rgba(255,255,255,0.08)',
          // Layered shadow: deep drop shadow (lift) + violet under-glow
          // (Vesper ambient flare) + indigo grazing light from above (top
          // hairline) + inset top highlight (glass refraction).
          boxShadow: [
            '0 14px 44px rgba(0,0,0,0.55)',
            '0 6px 28px rgba(108,71,255,0.20)',
            '0 -1px 0 rgba(165,180,252,0.06)',
            'inset 0 1px 0 rgba(255,255,255,0.06)',
          ].join(', '),
        }}
      >
      {tabs.map(({ to, match, label, Icon }) => {
        const active = isActive(match);
        const showBadge = label === 'Rooms' && unreadCount > 0;
        return (
          <NavLink
            key={to}
            to={to}
            className="relative flex flex-col items-center justify-center cursor-pointer"
            style={{
              flex: 1,
              padding: '5px 4px',
              gap: 1,
              color: active ? '#fff' : 'rgba(255,255,255,0.45)',
              textDecoration: 'none',
            }}
          >
            <div className="relative">
              {label === 'You' ? (
                <YouTabIcon active={active} />
              ) : (
                <Icon size={18} strokeWidth={active ? 2 : 1.5} />
              )}
              {showBadge && (
                <span
                  className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1"
                  style={{ lineHeight: 1 }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            {label !== 'You' && (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: active ? 600 : 500,
                  letterSpacing: 0.1,
                  lineHeight: 1,
                }}
              >
                {label}
              </span>
            )}
            {/* Vesper-gold "kiss" — a small dot under the active tab.
                Positioned absolutely so it doesn't add height to the bar. */}
            {active && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  bottom: 1,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 3,
                  height: 3,
                  borderRadius: 9999,
                  background: '#E5B259',
                  boxShadow: '0 0 6px rgba(229,178,89,0.7)',
                }}
              />
            )}
          </NavLink>
        );
      })}
      </div>
    </div>
  );
}
